import React, { useState, useEffect, useRef } from 'react';
import { providersAPI } from '../../api/providers';
import { uploadImage } from '../../utils/cloudinary';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Input from '../../components/common/Input';
import {
  PhotoIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Portfolio = () => {
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [providerId, setProviderId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const profile = await providersAPI.getMyProfile();
      setProviderId(profile.id);
      const data = await providersAPI.getPhotos(profile.id);
      setPhotos(data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (!file.type.startsWith('image/')) { setUploadError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image must be under 5MB'); return; }

    setUploading(true);
    try {
      const url = await uploadImage(file);
      const payload = { url };
      if (caption.trim()) payload.caption = caption.trim();
      await providersAPI.uploadPhoto(payload);
      toast.success('Photo added to portfolio');
      setCaption('');
      setUploadError('');
      if (fileRef.current) fileRef.current.value = '';
      const data = await providersAPI.getPhotos(providerId);
      setPhotos(data || []);
    } catch (error) {
      console.error('Error uploading:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await providersAPI.deletePhoto(photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleReorder = async (index, direction) => {
    const newPhotos = [...photos];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newPhotos.length) return;

    [newPhotos[index], newPhotos[targetIndex]] = [newPhotos[targetIndex], newPhotos[index]];
    setPhotos(newPhotos);

    try {
      await providersAPI.reorderPhotos(newPhotos.map(p => p.id));
    } catch (error) {
      console.error('Error reordering:', error);
      fetchPhotos();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Portfolio</h1>
          <p style={{ color: 'var(--gray-11)' }}>
            Showcase your best work to attract new clients
          </p>
        </div>

        <div className="flex gap-6" style={{ alignItems: 'flex-start' }}>
          {/* Left: Upload section */}
          <div style={{ width: '320px', flexShrink: 0 }}>
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)', position: 'sticky', top: '80px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '14px', textAlign: 'left' }}>
                Add Photo
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Input
                  label="Caption (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="e.g. Bridal makeup"
                />

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  style={{ display: 'none' }}
                />

                <Button
                  onClick={() => fileRef.current?.click()}
                  loading={uploading}
                  disabled={uploading}
                  fullWidth
                >
                  <PhotoIcon style={{ width: '16px', height: '16px' }} />
                  {uploading ? 'Uploading...' : 'Choose & Upload'}
                </Button>

                <p style={{ fontSize: '12px', color: 'var(--gray-9)', textAlign: 'left' }}>
                  JPG, PNG or WEBP. Max 5MB.
                </p>
                {uploadError && (
                  <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', textAlign: 'left', margin: 0 }}>
                    {uploadError}
                  </p>
                )}
              </div>

              {/* Stats */}
              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--gray-4)' }}>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', textAlign: 'left' }}>
                  <span style={{ fontWeight: 600, color: 'var(--orange-9)' }}>{photos.length}</span> photo{photos.length !== 1 ? 's' : ''} in your portfolio
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: '1px', backgroundColor: 'var(--orange-6)', flexShrink: 0, alignSelf: 'stretch', minHeight: '200px' }} />

          {/* Right: Gallery */}
          <div className="flex-1">
            {photos.length === 0 ? (
              <div className="text-center py-16">
                <PhotoIcon style={{ width: '48px', height: '48px', color: 'var(--gray-7)', margin: '0 auto 12px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '6px' }}>
                  No photos yet
                </h3>
                <p style={{ color: 'var(--gray-10)', fontSize: '14px' }}>
                  Upload your first photo to start building your portfolio
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="rounded-xl overflow-hidden group"
                    style={{ border: '1px solid var(--gray-4)', backgroundColor: 'var(--gray-2)', position: 'relative' }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption || 'Portfolio'}
                      style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }}
                    />

                    {/* Hover overlay with actions */}
                    <div
                      className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    >
                      {index > 0 && (
                        <button
                          onClick={() => handleReorder(index, 'up')}
                          style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.9)', border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Move up"
                        >
                          <ArrowUpIcon style={{ width: '16px', height: '16px', color: '#111' }} />
                        </button>
                      )}
                      {index < photos.length - 1 && (
                        <button
                          onClick={() => handleReorder(index, 'down')}
                          style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.9)', border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Move down"
                        >
                          <ArrowDownIcon style={{ width: '16px', height: '16px', color: '#111' }} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(photo.id)}
                        style={{
                          width: '32px', height: '32px', borderRadius: '50%',
                          backgroundColor: 'rgba(239,68,68,0.9)', border: 'none',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Delete"
                      >
                        <TrashIcon style={{ width: '16px', height: '16px', color: '#fff' }} />
                      </button>
                    </div>

                    {/* Caption */}
                    {photo.caption && (
                      <div style={{ padding: '8px 10px' }}>
                        <p style={{ fontSize: '13px', color: 'var(--gray-11)', margin: 0, textAlign: 'left' }}>
                          {photo.caption}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
